import { useCallback, useEffect, useRef } from 'react';

/**
 * Keeps keyboard focus in place across a retry. A "Try again" control usually lives in an error
 * state that unmounts once the retry succeeds, which drops focus to the document. Wrap the retry
 * with `retryWith` and attach `focusTargetRef` to what should take focus once the content is back
 * (a search input, or a heading with `tabIndex={-1}`). Focus moves only after a retry, never on an
 * ordinary load.
 */
export const useFocusAfterRetry = <TElement extends HTMLElement>(
  recovered: boolean
): {
  focusTargetRef: React.RefObject<TElement>;
  retryWith: (onRetry: () => void) => () => void;
} => {
  const focusTargetRef = useRef<TElement>(null);
  const retryingRef = useRef(false);

  useEffect(() => {
    if (!recovered || !retryingRef.current) return;
    retryingRef.current = false;
    focusTargetRef.current?.focus();
  }, [recovered]);

  const retryWith = useCallback(
    (onRetry: () => void) => () => {
      retryingRef.current = true;
      onRetry();
    },
    []
  );

  return { focusTargetRef, retryWith };
};
